import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../components/api';

export const useStatut = () => {
  const [statuts, setStatuts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    count: 0,
    total_pages: 0,
    current_page: 1,
    page_size: 10,
    has_next: false,
    has_previous: false
  });

  // Récupérer la liste des statuts avec pagination
  const fetchStatuts = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '10'
      });
      
      if (search) {
        params.append('search', search);
      }
      
      const response = await api.get(`/enseignant/status-enseignant/?${params}`);
      
      if (response.data.status === 'success') {
        setStatuts(response.data.data || []);
        setPagination(response.data.pagination || {});
      } else {
        setStatuts([]);
        setPagination({
          count: 0,
          total_pages: 0,
          current_page: 1,
          page_size: 10,
          has_next: false,
          has_previous: false
        });
      }
    } catch (error) {
      console.error("Erreur lors du chargement des statuts:", error);
      toast.error("Impossible de charger les statuts d'enseignants");
      setStatuts([]);
    } finally {
      setLoading(false);
    }
  };

  // Gérer la recherche avec pagination
  const handleSearch = (term) => {
    setSearchTerm(term);
    fetchStatuts(1, term);
  };

  // Changer de page
  const handlePageChange = (page) => {
    fetchStatuts(page, searchTerm);
  };

  // Créer un nouveau statut
  const createStatut = async (formData) => {
    setSubmitting(true);
    try {
      const response = await api.post('/enseignant/status-enseignant/', formData);
      
      // Recharger les statuts après création
      await fetchStatuts(pagination.current_page, searchTerm);
      
      toast.success("Statut créé avec succès!");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Erreur lors de la création du statut:", error);
      
      if (error.response?.data?.libelle) {
        toast.error("Ce statut existe déjà");
      } else {
        toast.error("Impossible de créer le statut");
      }
      
      return { success: false, error: error.response?.data };
    } finally {
      setSubmitting(false);
    }
  };

  // Modifier un statut
  const updateStatut = async (id, formData) => {
    setSubmitting(true);
    try {
      const response = await api.put(`/enseignant/details-status-ensignant/${id}/`, formData);
      
      // Recharger les statuts après modification
      await fetchStatuts(pagination.current_page, searchTerm);
      
      toast.success("Statut modifié avec succès!");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Erreur lors de la modification du statut:", error);
      
      if (error.response?.data?.libelle) {
        toast.error("Ce statut existe déjà");
      } else {
        toast.error("Impossible de modifier le statut");
      }
      
      return { success: false, error: error.response?.data };
    } finally {
      setSubmitting(false);
    }
  };

  // Supprimer un statut
  const deleteStatut = async (id) => {
    setDeleteLoading(true);
    try {
      await api.delete(`/enseignant/details-status-ensignant/${id}/`);
      
      // Recharger les statuts après suppression
      await fetchStatuts(pagination.current_page, searchTerm);
      
      toast.success("Statut supprimé avec succès!");
      return { success: true };
    } catch (error) {
      console.error("Erreur lors de la suppression du statut:", error);
      
      if (error.response?.status === 403) {
        toast.error("Vous n'avez pas la permission de supprimer ce statut");
      } else {
        toast.error("Impossible de supprimer le statut");
      }
      
      return { success: false, error: error.response?.data };
    } finally {
      setDeleteLoading(false);
    }
  };

  // Charger les statuts au montage du composant
  useEffect(() => {
    fetchStatuts();
  }, []);

  return {
    statuts,
    loading,
    submitting,
    deleteLoading,
    searchTerm,
    pagination,
    handleSearch,
    handlePageChange,
    createStatut,
    updateStatut,
    deleteStatut,
    fetchStatuts
  };
}; 